import React, { useEffect, useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  Box,
  TextField,
  Button,
  TablePagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import axios from "axios";
import PageHeader1 from "../../components/common/PageHeader1";
import { formatUTCDate, convertISTToUTCForAPI } from "../../utils/dateUtils";
import * as XLSX from "xlsx";

const FreeChatReports = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [searchType, setSearchType] = useState("fullName");
  const [appliedSearchQuery, setAppliedSearchQuery] = useState("");
  const [appliedSearchType, setAppliedSearchType] = useState("fullName");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });
  const [paginationDetail, setPaginationDetail] = useState({
    totalDocs: 0,
    totalPages: 0,
    page: 1,
    limit: 10,
  });
  const [userFreeChatTakenCount, setUserFreeChatTakenCount] = useState(0);
  const [userFreeChatNotTakenCount, setUserFreeChatNotTakenCount] = useState(0);

  const [startDate, setStartDate] = useState("");
  const [appliedStartDate, setAppliedStartDate] = useState("");

  const [endDate, setEndDate] = useState("");
  const [appliedEndDate, setAppliedEndDate] = useState("");

  const [isExporting, setIsExporting] = useState(false);

  const fetchData = async (overrides = {}) => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem("astroVani-admin-token");
      const params = {
        page: (overrides.page ?? paginationModel.page) + 1,
        limit: overrides.pageSize ?? paginationModel.pageSize,
      };

      if ((overrides.searchType ?? appliedSearchType) === "fullName") {
        params.fullName = overrides.searchQuery ?? appliedSearchQuery;
      } else if ((overrides.searchType ?? appliedSearchType) === "mobileNumber") {
        params.mobileNumber = overrides.searchQuery ?? appliedSearchQuery;
      }

      const start = overrides.startDate !== undefined ? overrides.startDate : appliedStartDate;
      const end = overrides.endDate !== undefined ? overrides.endDate : appliedEndDate;

      if (start) {
        params.startDate = convertISTToUTCForAPI(start, 'start');
      }
      if (end) {
        params.endDate = convertISTToUTCForAPI(end, 'end');
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/freeChatUsed`,
        {
          params,
          headers: {
            Authorization: token,
          },
        }
      );

      const res = response.data;
      setData(
        res.data?.users?.map((item, index) => ({
          ...item,
          id: item.userId || item._id || `${item.mobileNumber || "free-chat"}-${index}`,
          fullName: item.fullName || "-",
          email: item.email || "-",
          mobileNumber: item.mobileNumber || "-",
          freeChatCount: item.freeChatCount || 0,
          isFreeChatUsed: Boolean(item.isFreeChatUsed),
          createdAt: item.createdAt || null,
        })) || []
      );
      setPaginationDetail(
        res.paginationDetail || {
          totalDocs: 0,
          totalPages: 0,
          page: 1,
          limit: 10,
        }
      );
      setUserFreeChatTakenCount(res.data?.usersTakenFreeChat || 0);
      setUserFreeChatNotTakenCount(res.data?.usersNotTakenFreeChat || 0);
    } catch (err) {
      setError(err.message || "Failed to fetch data");
      toast.error(err.response?.data?.message || "Failed to fetch data");
      setData([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginationModel.page, paginationModel.pageSize, appliedSearchQuery, appliedSearchType, appliedStartDate, appliedEndDate]);

  const handleApplyFilters = () => {
    const trimmedSearchQuery = searchQuery.trim();

    setAppliedSearchQuery(trimmedSearchQuery);
    setAppliedSearchType(searchType);
    setAppliedStartDate(startDate);
    setAppliedEndDate(endDate);

    if (paginationModel.page !== 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      return;
    }

    if (
      trimmedSearchQuery === appliedSearchQuery &&
      searchType === appliedSearchType &&
      startDate === appliedStartDate &&
      endDate === appliedEndDate
    ) {
      fetchData({
        page: 0,
        pageSize: paginationModel.pageSize,
        searchQuery: trimmedSearchQuery,
        searchType,
        startDate,
        endDate,
      });
    }
  };

  const handleReset = () => {
    setSearchQuery("");
    setSearchType("fullName");
    setAppliedSearchQuery("");
    setAppliedSearchType("fullName");
    setStartDate("");
    setEndDate("");
    setAppliedStartDate("");
    setAppliedEndDate("");

    if (paginationModel.page !== 0) {
      setPaginationModel((prev) => ({ ...prev, page: 0 }));
      return;
    }

    if (appliedSearchQuery || appliedSearchType !== "fullName" || appliedStartDate || appliedEndDate) {
      fetchData({
        page: 0,
        pageSize: paginationModel.pageSize,
        searchQuery: "",
        searchType: "fullName",
        startDate: "",
        endDate: "",
      });
    }
  };

  const handleChangePage = (event, newPage) =>
    setPaginationModel((prev) => ({ ...prev, page: newPage }));

  const handleChangeRowsPerPage = (event) => {
    const newSize = parseInt(event.target.value, 10);
    setPaginationModel({ page: 0, pageSize: newSize });
  };

  const isFilterActive =
    searchQuery.trim() !== "" ||
    appliedSearchQuery.trim() !== "" ||
    searchType !== "fullName" ||
    appliedSearchType !== "fullName" ||
    startDate !== "" ||
    appliedStartDate !== "" ||
    endDate !== "" ||
    appliedEndDate !== "";

  const exportToExcel = async () => {
    setIsExporting(true);
    try {
      const token = localStorage.getItem("astroVani-admin-token");
      const params = {
        page: 1,
        limit: 100000,
      };

      if (appliedSearchQuery && appliedSearchQuery.trim()) {
        if (appliedSearchType === "mobileNumber") {
          params.mobileNumber = appliedSearchQuery;
        } else if (appliedSearchType === "fullName") {
          params.fullName = appliedSearchQuery;
        }
      }

      if (appliedStartDate) {
        params.startDate = convertISTToUTCForAPI(appliedStartDate, 'start');
      }
      if (appliedEndDate) {
        params.endDate = convertISTToUTCForAPI(appliedEndDate, 'end');
      }

      const response = await axios.get(
        `${process.env.REACT_APP_BASEURL}/admin/freeChatUsed`,
        {
          params,
          headers: { Authorization: token },
        }
      );

      const exportRows = response?.data?.data?.users || [];

      if (exportRows.length === 0) {
        alert("No data available to export");
        setIsExporting(false);
        return;
      }

      const exportData = exportRows.map((row, index) => ({
        "S.No": index + 1,
        "Date": row.createdAt ? formatUTCDate(row.createdAt) : "N/A",
        "Name": row.fullName || "-",
        "Mobile Number": row.mobileNumber || "-",
        "Free Chat Count": row.freeChatCount || 0,
        "Is Free Chat Used": row.freeChatCount && row.freeChatCount > 0 ? "Yes" : "No",
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "FreeChatReports");
      XLSX.writeFile(workbook, `Free_Chat_Reports_${new Date().toISOString().split('T')[0]}.xlsx`);
    } catch (error) {
      console.error("Export error:", error);
      alert("Failed to export data");
    } finally {
      setIsExporting(false);
    }
  };

  const rows = useMemo(
    () =>
      data.map((row, index) => ({
        ...row,
        sono: index + 1 + paginationModel.page * paginationModel.pageSize,
      })),
    [data, paginationModel.page, paginationModel.pageSize]
  );

  const columns = [
    {
      field: "sono",
      headerName: "S.No",
      width: 80,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "createdAt",
      headerName: "Date",
      width: 130,
      renderCell: (params) => (
        <div>{params.row.createdAt ? formatUTCDate(params.row.createdAt) : "N/A"}</div>
      ),
    },
    {
      field: "fullName",
      headerName: "Name",
      width: 200,
    },
    {
      field: "mobileNumber",
      headerName: "Mobile Number",
      width: 170,
    },
    {
      field: "freeChatCount",
      headerName: "Free Chat Count",
      width: 150,
      align: "center",
      headerAlign: "center",
    },
    {
      field: "isFreeChatUsed",
      headerName: "Is Free Chat Used",
      width: 160,
      align: "center",
      headerAlign: "center",
      renderCell: (params) => (
        <span
          style={{
            color: params.row.freeChatCount && params.row.freeChatCount > 0 ? "#4caf50" : "#9e9e9e",
            fontWeight: 600,
          }}
        >
          {params.row.freeChatCount && params.row.freeChatCount > 0 ? "Yes" : "No"}
        </span>
      ),
    },
  ];

  return (
    <div className="body d-flex">
      <div className="container-xxl">
        <PageHeader1 pagetitle="Free Chat Reports" />

        <Box
          sx={{
            display: "flex",
            gap: 2,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 220,
              p: 2,
              borderRadius: 2,
              backgroundColor: "#d4edda",
              border: "1px solid #c3e6cb",
            }}
          >
            <Box sx={{ fontSize: "0.875rem", color: "#155724", mb: 0.5 }}>
              Free Chat Taken
            </Box>
            <Box sx={{ fontSize: "1.5rem", fontWeight: 600, color: "#155724" }}>
              {userFreeChatTakenCount}
            </Box>
          </Box>

          <Box
            sx={{
              flex: 1,
              minWidth: 220,
              p: 2,
              borderRadius: 2,
              backgroundColor: "#f8d7da",
              border: "1px solid #f5c6cb",
            }}
          >
            <Box sx={{ fontSize: "0.875rem", color: "#721c24", mb: 0.5 }}>
              Free Chat Not Taken
            </Box>
            <Box sx={{ fontSize: "1.5rem", fontWeight: 600, color: "#721c24" }}>
              {userFreeChatNotTakenCount}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", flex: 1, alignItems: "center" }}>
          <FormControl size="small" sx={{ minWidth: 180 }}>
            <InputLabel>Search By</InputLabel>
            <Select
              value={searchType}
              label="Search By"
              onChange={(e) => setSearchType(e.target.value)}
              sx={{
                borderRadius: "8px",
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E7B242",
                },
              }}
            >
              <MenuItem value="fullName">Full Name</MenuItem>
              <MenuItem value="mobileNumber">Mobile Number</MenuItem>
            </Select>
          </FormControl>

          <TextField
            label="Search"
            variant="outlined"
            size="small"
            placeholder={searchType === "fullName" ? "Enter full name..." : "Enter mobile number..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") handleApplyFilters();
            }}
            sx={{
              width: 220,
              "& .MuiOutlinedInput-root": {
                "& fieldset": {
                  borderColor: "rgba(0, 0, 0, 0.23)",
                },
                "&:hover fieldset": {
                  borderColor: "rgba(0, 0, 0, 0.87)",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#E7B242",
                },
              },
            }}
          />

          <TextField
            label="Start Date"
            type="date"
            variant="outlined"
            size="small"
            value={startDate || ""}
            onChange={(e) => setStartDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: { xs: '140px', sm: '160px', md: '180px' },
              '& .MuiInputBase-input[type="date"]': {
                paddingRight: '8px',
                paddingLeft: '14px',
              },
            }}
          />
          <TextField
            label="End Date"
            type="date"
            variant="outlined"
            size="small"
            value={endDate || ""}
            onChange={(e) => setEndDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: { xs: '140px', sm: '160px', md: '180px' },
              '& .MuiInputBase-input[type="date"]': {
                paddingRight: '8px',
                paddingLeft: '14px',
              },
            }}
          />

        </Box>

        <Box
          sx={{
            display: "flex",
            gap: 2,
            alignItems: "center",
            justifyContent: "flex-end",
            marginTop: { xs: "10px", my: "10px" },
          }}
        >
          {isFilterActive && (
            <Button
              variant="outlined"
              onClick={handleReset}
              sx={{
                borderRadius: "8px",
                textTransform: "none",
                borderColor: "var(--primary-color, #E7B242)",
                color: "var(--primary-color, #E7B242)",
                "&:hover": {
                  borderColor: "var(--primary-color, #E7B242)",
                  backgroundColor: "rgba(231, 178, 66, 0.08)",
                },
              }}
            >
              Reset
            </Button>
          )}

          <Button
            variant="contained"
            onClick={handleApplyFilters}
            disabled={loading}
            className="btn btn-primary"
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              backgroundColor: "#E7B242",
              color: "#fff",
            }}
          >
            {loading ? (
              <>
                <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
                Applying...
              </>
            ) : (
              "Apply Filters"
            )}
          </Button>

          <Button
            variant="contained"
            onClick={exportToExcel}
            disabled={isExporting}
            sx={{
              borderRadius: "8px",
              textTransform: "none",
              backgroundColor: "#E7B242",
              color: "#fff",
              "&:hover": { backgroundColor: "#E7B242", opacity: 0.9 },
              "&:disabled": { backgroundColor: "#E7B242", opacity: 0.6 },
            }}
          >
            {isExporting ? (
              <>
                <CircularProgress size={16} sx={{ color: "#fff", mr: 1 }} />
                Exporting...
              </>
            ) : (
              "Export to Excel"
            )}
          </Button>
        </Box>

        <div
          className="card"
          style={{
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <div className="card-body" style={{ padding: 0, mt: "10px" }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                backgroundColor: "#f8f9fa",
                borderBottom: "2px solid #e9ecef",
              }}
            >
              <TablePagination
                component="div"
                count={paginationDetail.totalDocs || 0}
                page={paginationModel.page}
                onPageChange={handleChangePage}
                rowsPerPage={paginationModel.pageSize}
                onRowsPerPageChange={handleChangeRowsPerPage}
                rowsPerPageOptions={[10, 25, 50]}
              />
            </Box>

            <Box
              sx={{
                width: "100%",
                "& .MuiDataGrid-root": { border: "none" },
                "& .MuiDataGrid-columnHeaders": {
                  backgroundColor: "#f8f9fa",
                  borderBottom: "2px solid #e9ecef",
                  fontWeight: 600,
                },
              }}
            >
              <DataGrid
                autoHeight
                columns={columns}
                rows={rows}
                getRowId={(row) => row.id}
                rowCount={paginationDetail.totalDocs || 0}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={[10, 25, 50, 100]}
                paginationMode="server"
                loading={loading}
                disableRowSelectionOnClick
                hideFooterPagination
                hideFooter
                components={{
                  LoadingOverlay: CircularProgress,
                }}
                sx={{
                  "& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within": {
                    outline: "none",
                  },
                  "& .MuiDataGrid-cell": {
                    padding: "8px 16px",
                  },
                }}
              />
            </Box>

            {!loading && error && (
              <Box
                sx={{
                  px: 2,
                  py: 2,
                  color: "#d32f2f",
                  borderTop: "1px solid #f0f0f0",
                }}
              >
                {error}
              </Box>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FreeChatReports;
